import React from "react";
import { clsx } from "clsx";

import { ConsentModel } from "@/api";
import { ToggleChoice } from "@/design-system/components/ToggleChoice/ToggleChoice";

export type Choice<Value extends string> = {
  value: Value;
  title: string;
  description: string;
};

export function ChoiceCards<Value extends string>({
  label,
  choices,
  value,
  onChange,
}: {
  label: string;
  choices: Choice<Value>[];
  value: Value | undefined;
  onChange: (value: Value) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="grid grid-cols-2 gap-2"
    >
      {choices.map((choice) => {
        const selected = choice.value === value;
        return (
          <button
            key={choice.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(choice.value)}
            className={clsx(
              "hover:bg-surface-hover flex cursor-pointer flex-col gap-1 rounded-lg border p-4 text-left",
              selected
                ? "border-interactive-selected bg-surface-hover"
                : "border-borderSubtle",
            )}
          >
            <span className="text-label-md text-textPrimary">
              {choice.title}
            </span>
            <span className="text-body-sm text-textTertiary">
              {choice.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}

const consentModelOptions: { value: ConsentModel; label: string }[] = [
  { value: "opt-in", label: "Opt-in" },
  { value: "opt-out", label: "Opt-out" },
];

export function ConsentModelToggle({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: ConsentModel;
  onChange: (value: ConsentModel) => void;
  disabled?: boolean;
}) {
  return (
    <ToggleChoice
      ariaLabel={label}
      className="shrink-0"
      optionClassName="text-label-sm"
      options={consentModelOptions}
      value={consentModelOptions.find((option) => option.value === value)}
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.label}
      handleChange={(option) => onChange(option.value)}
      disabled={disabled}
    />
  );
}
